import { useEffect, useMemo, useState } from "react";
import {
  OVERLAY_NONE,
  REWARD_ONLY_ACC,
  heroPartKey,
  allPartIds,
  nextPartId,
  type AssetSlot,
  type OverlayCategory,
} from "@greg-chick/shared";
import {
  loadManifest,
  loadSlots,
  publishAssets,
  type Manifest,
  type ManifestEntry,
} from "../lib/contentApi";
import { useRegisterSave, discardDraft } from "../lib/saveBar";
import { loadScreenDraft } from "../lib/screenDraft";
import { SlotUploader } from "./SlotUploader";
import { HeroMaker } from "./HeroMaker";
import { PropFilePicker } from "./PropFilePicker";
import { ArtDraftBox } from "./ArtDraftBox";
import type { ArtKind } from "@greg-chick/shared";

/**
 * ➕ **직접 만들기** — 운영자가 자기 그림으로 새 항목을 만드는 곳(FR-ADMIN-04).
 *
 * ## 왜 따로 있나
 * 만들 수 있는 것들이 화면마다 흩어져 있었다 — 모자는 [그림 전체 목록] 깊은 곳, 친구는
 * [캐릭터], 꾸미기 물건은 또 다른 곳. "새 모자 하나 추가하고 싶다"는 사람이 어디로 가야
 * 하는지 알 수가 없었다. 여기 하나로 모은다: **무엇을 만들지 고르고 → 이름 짓고 → 그림 올리기.**
 *
 * ## 몇 개든 만들 수 있다
 * 예전엔 shared 카탈로그에 **빈 자리를 미리 뚫어 두고**(c5~c8·a5~a8) 그만큼만 채우게 했다.
 * 목록이 곧 검증이라 자리를 늘리려면 코드를 고쳐야 했다 — **콘텐츠를 코드가 막고 있었다.**
 * 다 차면 화면이 「개발자에게 자리를 늘려 달라고 하세요」라고 말했다(2026-08-23 신고).
 *
 * 이제 검증은 **모양**으로 한다(`c` + 숫자 · 불변식 7 유지). 그릴 수 있는지는 매니페스트가
 * 정한다 — 그림이 들어간 자리만 아이 화면에 선택지로 나온다(`pickableParts`).
 * 완성 캐릭터·마을 친구가 이미 그 방식이고, 모자·소품만 옛 목록을 보고 있었다.
 *
 * ## 이름은 그림에 붙여 저장한다
 * 별도 이름표를 두면 그림과 따로 놀다가 어긋난다. 매니페스트 엔트리의 `alt` 에 넣어
 * 그림과 한 몸으로 다닌다(다른 화면들도 같은 방식 — `npc.<id>.down` 의 alt 가 친구 이름).
 */

type KindId = "hero" | "hair" | "acc" | "npc" | "decor" | "bg";

const KINDS: { id: KindId; icon: string; label: string; hint: string }[] = [
  { id: "hero", icon: "🧒", label: "히어로 캐릭터", hint: "아이가 고르는 완성 캐릭터" },
  { id: "hair", icon: "🎩", label: "모자·머리장식", hint: "아이가 머리에 쓰는 것" },
  { id: "acc", icon: "🎒", label: "소품", hint: "아이가 들거나 매는 것" },
  { id: "npc", icon: "🐣", label: "마을 친구", hint: "체험공간을 돌아다니는 친구" },
  { id: "decor", icon: "🌳", label: "꾸미기 물건", hint: "마을에 놓는 나무·바위 같은 것" },
  { id: "bg", icon: "🏞️", label: "마을 배경", hint: "마을 뒤에 깔리는 큰 그림" },
];

/** 아이 장식은 방향마다 다르게 보인다 — 앞은 필수, 옆·뒤는 있으면 쓰고 없으면 앞을 돌려 쓴다. */
const OVERLAY_DIRS = [
  { sfx: "", label: "앞모습", need: true },
  { sfx: ".side", label: "옆모습", need: false },
  { sfx: ".up", label: "뒷모습", need: false },
];
/** 마을 친구는 세 방향이 다 있어야 걸을 때 자연스럽다. */
const NPC_DIRS = [
  { sfx: ".down", label: "앞(정면)", need: true },
  { sfx: ".side", label: "옆", need: false },
  { sfx: ".up", label: "뒤", need: false },
];
const ZONES = [
  { id: "lobby", label: "마을 입구" },
  { id: "gyeongju", label: "경주" },
  { id: "book", label: "책방" },
  { id: "goods", label: "굿즈 가게" },
  { id: "event", label: "행사장" },
];

/** 한글 이름 → 안전한 id. 영숫자가 있으면 그걸, 없으면(한글만) 안정적인 짧은 해시. */
function makeId(title: string): string {
  const ascii = title.toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 20);
  if (ascii) return ascii;
  let h = 0;
  for (let i = 0; i < title.length; i++) h = (h * 31 + title.charCodeAt(i)) >>> 0;
  return "c" + h.toString(36);
}

export function CustomResourcePanel({ onGoDeco }: { onGoDeco?: () => void }) {
  const [manifest, setManifest] = useState<Manifest>({ version: 0, assets: {} });
  const [slots, setSlots] = useState<AssetSlot[]>([]);
  const [dirty, setDirty] = useState<Set<string>>(new Set());
  const [err, setErr] = useState<string | null>(null);
  const [kind, setKind] = useState<KindId>(
    () => (localStorage.getItem("gc_admin_custom_kind") as KindId) || "hair",
  );
  const [name, setName] = useState("");
  /** 🏞️ 배경은 마을마다 자리가 따로다 — AI 그림을 어느 마을에 넣을지. */
  const [bgZone, setBgZone] = useState(ZONES[0]!.id);
  const [bgDrop, setBgDrop] = useState<File | null>(null);

  useEffect(() => {
    /**
     * 🔴 **저장해 둔 것을 불러온 뒤에 덮어쓰던 버그.**
     *
     * 그림을 넣고 [💾 저장하기]를 눌러도 새로고침하면 사라졌다(2026-08-23 신고). 두 가지가 겹쳤다:
     *  ① 보관본은 [불러오기]를 **눌러야** 들어왔다 — 저장한 사람은 이미 저장했다고 생각한다
     *  ② 눌러도 이 효과가 나중에 끝나면서 `setManifest(불러온 것)` 이 **덮어썼다**(경합)
     *
     * 그래서 여기서 **읽고 나서 그 위에 보관본을 얹는다.** 순서가 고정이라 경합이 없고,
     * 운영자는 아무것도 안 눌러도 하던 자리에서 이어 한다. 되돌리려면 저장바의 [버리기].
     */
    void loadManifest()
      .then((m) => {
        const kept = loadScreenDraft<{ entries?: Record<string, ManifestEntry | null>; dirty?: string[] }>(
          "custom",
        );
        const e = kept?.data?.entries;
        if (!e) return setManifest(m);
        const next = { ...m.assets };
        for (const [k, v] of Object.entries(e)) {
          if (v) next[k] = v;
          else delete next[k]; // 지운 자리는 지운 채로(키를 빼면 「안 지운 것」이 된다)
        }
        setManifest({ ...m, assets: next });
        setDirty(new Set(kept.data.dirty ?? Object.keys(e)));
        setErr(null);
      })
      .catch((e: Error) => setErr(e.message));
    void loadSlots().then(setSlots).catch(() => setSlots([]));
  }, []);

  const a = manifest.assets as Record<string, ManifestEntry>;
  const slotByKey = useMemo(() => Object.fromEntries(slots.map((s) => [s.key, s])), [slots]);

  /**
   * 슬롯 정의를 아직 못 받았을 때 **화면을 터뜨리지 않는다.**
   *
   * `slotByKey["world.background"]!.spec` 처럼 `!` 로 넘기면 목록이 오기 전에 그리는 순간
   * `Cannot read properties of undefined (reading 'spec')` 로 화면이 통째로 죽는다.
   * 느린 연결이나 **새로고침 직후**에 실제로 그랬다(2026-08-11 신고).
   *
   * ⚠️ 이 화면은 **자기 슬롯을 따로 부른다**(아래 useEffect). 다른 화면들은 App 이
   * 다 받고 나서 그리므로 이 순간이 없다 — 같은 코드인데 여기서만 터진 이유다.
   *
   * 「아직 오는 중」과 「정의가 없음」은 운영자가 할 일이 다르므로 나눠 말한다.
   */
  const slotGuard = (key: string) =>
    slotByKey[key] ? null : (
      <p className="hint">
        {slots.length === 0
          ? "⏳ 슬롯 정의를 불러오는 중이에요…"
          : `⚠️ 「${key}」 슬롯 정의가 없어요(content/asset-slots.json).`}
      </p>
    );

  const setEntry = (key: string, entry: ManifestEntry | null) => {
    setManifest((m) => {
      const next = { ...m.assets };
      if (entry) next[key] = entry;
      else delete next[key];
      return { ...m, assets: next };
    });
    setDirty((d) => new Set(d).add(key));
  };

  async function publish() {
    const out = await publishAssets(manifest.assets);
    setManifest((m) => ({ ...m, version: out.version }));
    setDirty(new Set());
    // 웹에 올라갔으면 보관본은 할 일을 다했다 — 안 버리면 다음에 들어올 때
    // 이미 올린 것이 「아직 안 올린 것」으로 되살아난다.
    discardDraft("custom");
  }
  useRegisterSave({
    id: "custom",
    label: "직접 만든 것",
    dirty: dirty.size,
    save: publish,
    // 💾 바꾼 그림 자리만 담는다 — 그림 파일 자체는 올릴 때 이미 서버에 있다.
    //    지운 자리는 null 로 남긴다(키를 빼면 "안 지운 것"이 된다).
    draft: {
      data: () => ({
        entries: Object.fromEntries([...dirty].map((k) => [k, a[k] ?? null])),
        dirty: [...dirty],
      }),
      restore: (d) => {
        const v = d as { entries?: Record<string, ManifestEntry | null>; dirty?: string[] };
        if (!v?.entries) return;
        setManifest((m) => {
          const next = { ...m.assets };
          for (const [k, e] of Object.entries(v.entries!)) {
            if (e) next[k] = e;
            else delete next[k];
          }
          return { ...m, assets: next };
        });
        setDirty(new Set(v.dirty ?? Object.keys(v.entries!)));
      },
    },
  });

  const chooseKind = (k: KindId) => {
    setKind(k);
    setName("");
    localStorage.setItem("gc_admin_custom_kind", k);
  };

  return (
    <section className="panel cres">
      {err ? <p className="hint">⚠️ 목록을 불러오지 못했어요 — {err}</p> : null}

      {/* ① 무엇을 만들까 */}
      <div className="cres__kinds" role="group" aria-label="무엇을 만들까">
        {KINDS.map((k) => (
          <button
            key={k.id}
            type="button"
            className="cres__kind"
            data-on={kind === k.id || undefined}
            onClick={() => chooseKind(k.id)}
          >
            <span aria-hidden="true">{k.icon}</span>
            <b>{k.label}</b>
            <small>{k.hint}</small>
          </button>
        ))}
      </div>

      {kind === "hero" ? (
        <HeroMaker
          spec={slotByKey["hero.base"]?.spec}
          assets={a}
          onChange={setEntry}
          name={name}
          setName={setName}
        />
      ) : kind === "hair" || kind === "acc" ? (
        <OverlayMaker
          cat={kind}
          assets={a}
          spec={slotByKey["hero.part"]}
          onChange={setEntry}
          name={name}
          setName={setName}
        />
      ) : kind === "npc" ? (
        <NamedMaker
          title="마을 친구"
          hint="아이 전신이 프레임을 거의 채우고 발끝이 아래에 닿게. 앞모습만 올려도 돼요 — 옆·뒤가 없으면 앞모습으로 나와요."
          prefix="npc"
          repKeySuffix=".down"
          artKind="npc"
          dirs={NPC_DIRS}
          assets={a}
          spec={slotByKey["npc.sprite"] ?? slotByKey["char.dir"]}
          onChange={setEntry}
          name={name}
          setName={setName}
          after={
            onGoDeco ? (
              <p className="hint">
                만든 친구를 마을에 세우려면 <b>[🗺️ 체험공간 배치]</b>에서 끌어다 놓으세요.
              </p>
            ) : null
          }
        />
      ) : kind === "decor" ? (
        <>
          {/* 폴더에서 **여러 장 한꺼번에** — 체험공간 배치와 같은 부품(이름은 파일명에서 자동).
              한 장씩 이름을 지어 올리고 싶으면 아래 칸을 쓰면 된다. */}
          {slotGuard("decor")}
          {slotByKey["decor"] ? (
            <div className="cres__make">
              <h2 className="as-sec__h">내 컴퓨터에서 가져오기</h2>
              <PropFilePicker
                spec={slotByKey["decor"].spec}
                takenKeys={Object.keys(a)}
                onAdded={setEntry}
              />
            </div>
          ) : null}
        <NamedMaker
          title="꾸미기 물건"
          hint="투명 배경 그림 한 장. 마을에 놓으면 아이가 그 앞뒤로 지나다녀요."
          prefix="decor"
          repKeySuffix=""
          artKind="decor"
          dirs={[{ sfx: "", label: "그림", need: true }]}
          assets={a}
          spec={slotByKey["decor"]}
          onChange={setEntry}
          name={name}
          setName={setName}
          after={
            <p className="hint">
              놓는 자리는 <b>[🗺️ 체험공간 배치]</b>에서 정해요 — 거기서도 바로 올릴 수 있어요.
            </p>
          }
        />
        </>
      ) : (
        <div className="cres__make">
          <p className="hint">
            마을마다 <b>뒤에 깔리는 큰 그림</b>을 바꿔요. 새로 만드는 게 아니라 <b>바꿔 끼우는</b> 것이라
            마을을 골라 올리면 돼요.
          </p>
          {/* 🎨 배경은 마을마다 자리가 따로다 — **어느 마을에 넣을지** 먼저 고르게 한다.
              마을마다 AI 칸을 하나씩 두면 같은 상자가 다섯 개라 화면이 어려워진다. */}
          <label className="cres__name">
            어느 마을에 넣을까요?
            <select value={bgZone} onChange={(e) => setBgZone(e.target.value)}>
              {ZONES.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.label}
                </option>
              ))}
            </select>
          </label>
          <ArtDraftBox kind="bg" onPick={setBgDrop} />
          <div className="grid">
            {slotGuard("world.background")}
            {ZONES.map((z) => slotByKey["world.background"] && (
              <SlotUploader
                key={z.id}
                slotKey={`world.background.${z.id}`}
                label={z.label}
                spec={slotByKey["world.background"].spec}
                entry={a[`world.background.${z.id}`]}
                onChange={setEntry}
                incoming={z.id === bgZone ? bgDrop : null}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * 모자·소품 만들기 — **비어 있는 자리**를 하나 집어 그림을 넣는다.
 *
 * 이미 만든 것도 같이 보여 준다. 안 그러면 "아까 만든 모자를 고치려면 어디로 가지"가 된다.
 */
function OverlayMaker({
  cat,
  assets,
  spec,
  onChange,
  name,
  setName,
}: {
  cat: OverlayCategory;
  assets: Record<string, ManifestEntry>;
  spec?: AssetSlot;
  onChange: (key: string, entry: ManifestEntry | null) => void;
  name: string;
  setName: (v: string) => void;
}) {
  const label = cat === "hair" ? "모자·머리장식" : "소품";
  const keys = useMemo(() => Object.keys(assets), [assets]);
  /** 이미 그림이 든 것. "안 씀"(c0·a0)과 완주 메달(a4)은 뺀다 — 뜻이 정해진 자리다. */
  const used = useMemo(
    () =>
      allPartIds(cat, keys).filter(
        (id) => id !== OVERLAY_NONE[cat] && id !== REWARD_ONLY_ACC && assets[heroPartKey(cat, id)]?.url,
      ),
    [cat, keys, assets],
  );
  // 새로 만들 자리는 **늘 하나 준비돼 있다** — 안 쓰는 가장 작은 번호(지운 자리를 다시 쓴다).
  const target = useMemo(() => nextPartId(cat, keys), [cat, keys]);
  /** 🎨 AI·붙여넣기로 받은 그림 — **앞모습 칸**으로 보낸다([파일 고르기]와 같은 길). */
  const [drop, setDrop] = useState<File | null>(null);

  if (!spec) return <p className="hint">⚠️ 히어로 파츠 슬롯 정의가 없어요(content/asset-slots.json).</p>;

  return (
    <div className="cres__make">
      <h2 className="as-sec__h">새 {label} 만들기</h2>
      {(
        <>
          <label className="cres__name">
            이름
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={cat === "hair" ? "예: 밀짚모자" : "예: 나무 지팡이"}
              maxLength={20}
            />
          </label>
          <p className="hint">
            <b>정사각 투명 그림</b> — 아이 위에 얹힐 <b>제자리</b>(머리 위·가슴 옆)에 그려 주세요.
            자리는 나중에 <b>[🧸 캐릭터 → 🎩 모자·소품]</b>에서 보면서 미세 조정할 수 있어요.
            {" "}(지금 만드는 자리 <code>{target}</code> · <b>몇 개든 만들 수 있어요</b>)
          </p>
          {/* 🖼️ 이미 있는 같은 종류를 참고로 — 「지금까지 만든 모자들처럼」이 가장 잘 맞는다 */}
          <ArtDraftBox
            kind={cat}
            name={name}
            onPick={setDrop}
            refs={used
              .map((id) => ({
                key: heroPartKey(cat, id),
                url: assets[heroPartKey(cat, id)]?.url ?? "",
                label: assets[heroPartKey(cat, id)]?.alt || id,
              }))
              .filter((r) => !!r.url)}
          />
          <div className="grid">
            {OVERLAY_DIRS.map((d) => (
              <SlotUploader
                key={d.sfx}
                slotKey={heroPartKey(cat, target!) + d.sfx}
                label={`${d.label}${d.need ? "" : " (없으면 앞모습을 써요)"}`}
                spec={spec.spec}
                alt={name || undefined}
                entry={assets[heroPartKey(cat, target!) + d.sfx]}
                onChange={onChange}
                // 🎨 AI·붙여넣기 그림은 **앞모습**으로 — 옆·뒤는 앞을 돌려 쓰는 폴백이 이미 있다
                incoming={d.sfx === "" ? drop : null}
              />
            ))}
          </div>
        </>
      )}

      {used.length > 0 ? (
        <>
          <h2 className="as-sec__h">이미 있는 {label}</h2>
          <div className="grid">
            {used.map((id) => (
              <SlotUploader
                key={id}
                slotKey={heroPartKey(cat, id)}
                label={assets[heroPartKey(cat, id)]?.alt || id}
                spec={spec.spec}
                entry={assets[heroPartKey(cat, id)]}
                onChange={onChange}
                onRemove={() => {
                  if (!window.confirm(`'${assets[heroPartKey(cat, id)]?.alt || id}' 을(를) 지울까요? 아이 화면에서 사라져요.`)) return;
                  // 방향 그림까지 같이 지운다 — 앞모습만 지우면 옆·뒤가 유령으로 남는다
                  OVERLAY_DIRS.forEach((d) => onChange(heroPartKey(cat, id) + d.sfx, null));
                }}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

/** 이름을 지어 새 id 를 만드는 종류(친구·꾸미기 물건). id 는 이름에서 자동으로 나온다. */
function NamedMaker({
  title,
  hint,
  prefix,
  repKeySuffix,
  dirs,
  assets,
  spec,
  onChange,
  name,
  setName,
  after,
  artKind,
}: {
  title: string;
  hint: string;
  prefix: string;
  repKeySuffix: string;
  dirs: { sfx: string; label: string; need: boolean }[];
  assets: Record<string, ManifestEntry>;
  spec?: AssetSlot;
  onChange: (key: string, entry: ManifestEntry | null) => void;
  name: string;
  setName: (v: string) => void;
  after?: React.ReactNode;
  /** 🎨 AI 로 그릴 수 있는 종류면 그 id. 없으면 AI 칸을 안 보여 준다. */
  artKind?: ArtKind;
}) {
  const id = name.trim() ? makeId(name.trim()) : "";
  /** 🎨 AI·붙여넣기로 받은 그림 — **첫 방향 칸**으로 보낸다. */
  const [drop, setDrop] = useState<File | null>(null);
  /** 이미 만들어 둔 것 — 매니페스트에서 찾는다(따로 목록을 두면 그림과 어긋난다). */
  const mine = useMemo(() => {
    const re = new RegExp(`^${prefix}\\.([a-z0-9_]+)`);
    const seen: string[] = [];
    for (const k of Object.keys(assets)) {
      const m = re.exec(k);
      if (m?.[1] && !seen.includes(m[1])) seen.push(m[1]);
    }
    return seen;
  }, [assets, prefix]);

  if (!spec) return <p className="hint">⚠️ 이 종류의 슬롯 정의가 없어요(content/asset-slots.json).</p>;

  return (
    <div className="cres__make">
      <h2 className="as-sec__h">새 {title} 만들기</h2>
      <label className="cres__name">
        이름
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="예: 아기 여우" maxLength={20} />
      </label>
      <p className="hint">{hint}</p>
      {artKind ? (
        <ArtDraftBox
          kind={artKind}
          name={name}
          onPick={setDrop}
          refs={mine
            .map((mid) => ({
              key: `${prefix}.${mid}`,
              url: assets[`${prefix}.${mid}${repKeySuffix}`]?.url ?? "",
              label: assets[`${prefix}.${mid}${repKeySuffix}`]?.alt || mid,
            }))
            .filter((r) => !!r.url)}
        />
      ) : null}
      {id ? (
        <div className="grid">
          {dirs.map((d, i) => (
            <SlotUploader
              key={d.sfx}
              slotKey={`${prefix}.${id}${d.sfx}`}
              label={`${d.label}${d.need ? "" : " (없어도 돼요)"}`}
              spec={spec.spec}
              alt={name}
              entry={assets[`${prefix}.${id}${d.sfx}`]}
              onChange={onChange}
              incoming={i === 0 ? drop : null}
            />
          ))}
        </div>
      ) : (
        <p className="hint">먼저 이름을 지어 주세요 — 이름을 넣으면 그림 올리는 칸이 나와요.</p>
      )}
      {after}

      {mine.length > 0 ? (
        <>
          <h2 className="as-sec__h">이미 있는 {title}</h2>
          <div className="grid">
            {mine.map((mid) => (
              <SlotUploader
                key={mid}
                slotKey={`${prefix}.${mid}${repKeySuffix}`}
                label={assets[`${prefix}.${mid}${repKeySuffix}`]?.alt || mid}
                spec={spec.spec}
                entry={assets[`${prefix}.${mid}${repKeySuffix}`]}
                onChange={onChange}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
